import React, { useRef } from 'react'

import { useLastData } from '@/hooks/useLastData'
import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'
import { FixedSizeList } from 'react-window'

import { Box, Text } from '@hivewatch/dark'

import { GlobalIncidentsQuery } from '@/graphql/generated/operations'
import { useAppDispatch } from '@/redux/hooks'
import { openIncidentDrawer, setIncident } from '@/redux/ui/uiSlice'
import { isManualIncident } from '@/utils/incidents'

import { getNotificationsTabTokens } from '../../styles/tokens'
import { IncidentsListItem } from './IncidentsListItem'

// Height of a single ActiveIncidentCard row (py 12 + title 20 + gap 6 +
// subtitle ~16 + py 12 ≈ 66); the row's bottom border is the divider.
const ITEM_SIZE = 72
const LIST_HEIGHT = 500

interface IncidentsListIProps {
  data: GlobalIncidentsQuery
  isLoading: boolean
}

const FixedListDiv = ({
  children,
  ...props
}: {
  children: React.ReactNode
  [key: string]: unknown
}) => {
  return (
    <div data-testid='notificationsTab_activeIncidents_list' {...props}>
      {children}
    </div>
  )
}

export const IncidentsList = ({ data, isLoading }: IncidentsListIProps) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const tokens = getNotificationsTabTokens(colorScheme)
  const listRef = useRef(null)
  const lastData = useLastData(data)

  const incidents =
    lastData &&
    [...lastData?.incidents?.edges]
      ?.sort(
        (a, b) =>
          new Date(b?.node?.createdAt).valueOf() -
          new Date(a?.node?.createdAt).valueOf()
      )
      ?.map((i) => ({
        id: i?.node?.id,
        name: i?.node?.devices[0]?.name ?? 'Manual',
        title: i?.node?.name,
        dateTime: i?.node?.createdAt,
        facilityName: i?.node?.facility?.name,
        facility: i?.node?.facility?.shortName, // TODO - Update to reflect that this is the short name, and not a Facility object
        type: i?.node?.type,
        isManualIncident: isManualIncident({
          incidentType: i?.node?.incidentType,
          manualIncidentCategoryId: i?.node?.manualIncidentCategoryId,
        }),
        slug: i?.node?.incidentType?.slug,
        displayId: i?.node?.displayId,
        handleClick: () => openAlertIncidentDrawer(i?.node?.id),
      }))

  const openAlertIncidentDrawer = (id: string) => {
    dispatch(setIncident({ id }))
    dispatch(openIncidentDrawer())
  }

  return (
    <Box bg={tokens.surface} data-testid='IncidentsList:container' h='full'>
      <Box bg={tokens.surface} pos='relative'>
        {isLoading && !lastData ? (
          <Text
            color={tokens.textSecondary}
            mt='3'
            textAlign='center'
            textStyle='body-bold'
          >
            {t('common:notification_incidents_loading')}
          </Text>
        ) : incidents?.length > 0 ? (
          <FixedSizeList
            height={LIST_HEIGHT}
            itemCount={incidents.length}
            itemData={incidents}
            itemSize={ITEM_SIZE}
            outerElementType={FixedListDiv}
            ref={listRef}
          >
            {IncidentsListItem}
          </FixedSizeList>
        ) : (
          <Text
            color={tokens.textSecondary}
            mt='3'
            textAlign='center'
            textStyle='body-bold'
          >
            {t('common:notification_incidents_list_empty')}
          </Text>
        )}
      </Box>
    </Box>
  )
}
